/**
 * The route table — frontend.md §2.3.
 *
 * Routes mirror the six surfaces (+ Settings, + the Agent-9 editor/chat mounts). Paths are
 * kebab-case and centralized here (conventions.md §2.3 — "route constants centralized in
 * `routes.ts`"). Surface state — search query/filters/mode, selected entity/tag, dashboard
 * scope, graph focal note/radius — is URL-encoded in the path + query string so it is
 * shareable and restorable (frontend.md §2.2); this module owns the *path* constants and
 * the small builders, not the query-string schema (each surface owns its own params).
 */

/** The stable surface identifiers — used by the nav, the command palette, `useUiStore`. */
export type SurfaceId =
  | 'search'
  | 'browse'
  | 'dashboards'
  | 'graph'
  | 'health'
  | 'sources'
  | 'settings'
  | 'editor'
  | 'chat';

/** The route-path constants. Optional `:param?` segments are filled by the builders below. */
export const ROUTES = {
  search: '/search',
  browseEntities: '/browse/entities',
  browseTags: '/browse/tags',
  dashboards: '/dashboards',
  graph: '/graph',
  health: '/health',
  sources: '/sources',
  settings: '/settings',
  /** Agent-9 owns the editor/chat *elements*; the frontend owns the *route slots* (ShellMount). */
  editor: '/editor',
  chat: '/chat',
  /** Clerk-hosted-in-our-app auth pages. The marketing site's CTAs land at
   *  /sign-in?redirect_url=/editor (Clerk consumes redirect_url and lands the
   *  user there after a successful sign-in). */
  signIn: '/sign-in',
  signUp: '/sign-up',
} as const;

/** The `react-router` path patterns — the data-router route definitions use these. */
export const ROUTE_PATTERNS = {
  search: '/search',
  browseEntities: '/browse/entities/:entityId?',
  browseTags: '/browse/tags/:tagPath?',
  dashboards: '/dashboards/:dashboardId?',
  // Graph v0.2 is the new global view; the optional focalNoteId path param is kept for the
  // legacy ego-graph mode the old `<GraphSurface>` consumed (now unmounted by default — the
  // v2 surface is global). Keeping the pattern means deep links from older sessions still
  // resolve to /graph without 404.
  graph: '/graph/:focalNoteId?',
  health: '/health',
  sources: '/sources',
  settings: '/settings',
  editor: '/editor/:noteId?',
  chat: '/chat',
  // Clerk's <SignIn/> and <SignUp/> components handle their own internal routing
  // (verification step, MFA prompt, profile completion, etc.) via a path-prefix
  // pattern (/sign-in/*). The router pattern uses /* so React Router defers all
  // sub-paths to the Clerk component, which then handles them client-side.
  signIn: '/sign-in/*',
  signUp: '/sign-up/*',
} as const;

// --- path builders — typed, so a surface never string-concatenates a route -------

export const toSearch = (queryString?: string): string =>
  queryString ? `${ROUTES.search}?${queryString}` : ROUTES.search;

/** Build a search URL pre-filtered to a tag path. The search surface reads the `tag` query
 *  param (multi-valued — `?tag=a&tag=b`) into `filters.tagPaths` via `searchUrlState`. Used
 *  by the preview pane's hashtag-click handler to navigate "search for this tag". */
export const toSearchTag = (tagPath: string): string =>
  `${ROUTES.search}?tag=${encodeURIComponent(tagPath)}`;

export const toEntity = (entityId?: string): string =>
  entityId ? `${ROUTES.browseEntities}/${entityId}` : ROUTES.browseEntities;

export const toTag = (tagPath?: string): string =>
  tagPath ? `${ROUTES.browseTags}/${encodeURIComponent(tagPath)}` : ROUTES.browseTags;

export const toDashboard = (dashboardId?: string): string =>
  dashboardId ? `${ROUTES.dashboards}/${dashboardId}` : ROUTES.dashboards;

export const toGraph = (focalNoteId?: string, radius?: number): string => {
  if (!focalNoteId) return ROUTES.graph;
  const base = `${ROUTES.graph}/${focalNoteId}`;
  return radius ? `${base}?radius=${radius}` : base;
};

export const toHealthPanel = (panel?: string): string =>
  panel ? `${ROUTES.health}?panel=${panel}` : ROUTES.health;

export const toSettingsSection = (section?: string): string =>
  section ? `${ROUTES.settings}?section=${section}` : ROUTES.settings;

export const toEditor = (noteId?: string, conflict?: boolean): string => {
  if (!noteId) return ROUTES.editor;
  const base = `${ROUTES.editor}/${noteId}`;
  return conflict ? `${base}?conflict=1` : base;
};

/** Map a pathname back to its `SurfaceId` — used by `<AppShell>` to sync `useUiStore`.
 *
 *  The default falls through to ``'editor'`` (the v1 home — Joplin-shape note authoring is the
 *  primary surface, matching `<RouteOutlet>`'s `/` redirect).
 */
export function surfaceForPath(pathname: string): SurfaceId {
  if (pathname.startsWith('/search')) return 'search';
  if (pathname.startsWith('/browse')) return 'browse';
  if (pathname.startsWith('/dashboards')) return 'dashboards';
  if (pathname.startsWith('/graph')) return 'graph';
  if (pathname.startsWith('/health')) return 'health';
  if (pathname.startsWith('/sources')) return 'sources';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/chat')) return 'chat';
  return 'editor';
}
